import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { employerReviewSectionSafeError } from '../../lib/employerReview/employerReviewPanelState';
import { reportClientError } from '../../lib/telemetry';

type Props = {
  children: ReactNode;
  sectionLabel: string;
  onRetry?: () => void;
};

type State = {
  error: Error | null;
};

/**
 * Section-level render guard for employer review — never exposes raw exception text in production.
 */
export class EmployerReviewSectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportClientError('admin.EmployerReviewSectionErrorBoundary', error, {
      section: this.props.sectionLabel,
      componentStack: errorInfo.componentStack,
    });
  }

  private readonly handleRetry = (): void => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    const safe = employerReviewSectionSafeError(this.state.error);

    return (
      <Alert
        severity="warning"
        role="alert"
        sx={{
          color: 'text.primary',
          '& .MuiAlert-message': { width: '100%' },
        }}
        action={
          <Button color="inherit" size="small" onClick={this.handleRetry} sx={{ fontWeight: 600, minHeight: 36 }}>
            Try again
          </Button>
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          {this.props.sectionLabel} couldn&apos;t be displayed
        </AlertTitle>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {safe.message}
        </Typography>
        {safe.correlationId ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontFamily: 'monospace' }}>
            Reference: {safe.correlationId}
          </Typography>
        ) : null}
        {import.meta.env.DEV && this.state.error.stack ? (
          <Box component="details" sx={{ mt: 1 }}>
            <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>
              Developer stack
            </Typography>
            <Box component="pre" sx={{ fontSize: 10, overflow: 'auto', mt: 0.5 }}>
              {this.state.error.stack}
            </Box>
          </Box>
        ) : null}
      </Alert>
    );
  }
}
